<script>
// X199: a v-select / v-autocomplete for ids whose items load async. It is the same Vuetify field (same density,
// variant, label, height — nothing shifts), but an id whose item is not in hand yet is drawn as a skeleton bar (or a
// skeleton chip) and the field is read-only while its list or the by-ids fetch is loading. A raw id is never shown.
//   <tl-async-select v-model="ids" :items="options" :loading="pending" :known="namesWeHave" :fetch-by-ids="byIds"/>
//   <tl-async-select searchable multiple chips …/>   (searchable = v-autocomplete; every other v-select /
//   v-autocomplete prop or slot passes through, the native `autocomplete` attribute included)
// `known`: items the caller already has for the selected ids (same shape as items). `fetchByIds(ids)`: resolves the
// items for ids the list misses (asked once per id, after the list has answered).
import {defineComponent, h} from 'vue';
import {VAutocomplete, VChip, VSelect} from 'vuetify/components';
import {useAsyncSelection} from '~/composables/useAsyncSelection';

const SKELETON_BAR = 'tl-async-select__bar';

export default defineComponent({
  name: 'TlAsyncSelect',
  inheritAttrs: false,
  props: {
    searchable: {type: Boolean, default: false},
    modelValue: {type: null, default: undefined},
    items: {type: Array, default: () => []},
    known: {type: Array, default: () => []},
    loading: {type: [Boolean, String], default: false},
    fetchByIds: {type: Function, default: null},
    itemValue: {type: [String, Function], default: 'value'},
    itemTitle: {type: [String, Function], default: 'title'},
    multiple: {type: Boolean, default: false},
    chips: {type: Boolean, default: false},
    returnObject: {type: Boolean, default: false},
    readonly: {type: Boolean, default: false},
    unavailableText: {type: String, default: 'Unavailable'},
  },
  emits: ['update:modelValue'],
  setup(props, {attrs, slots, emit}) {
    const state = useAsyncSelection({
      model: () => props.modelValue,
      items: () => props.items,
      known: () => props.known,
      loading: () => props.loading,
      fetchByIds: () => props.fetchByIds,
      itemValue: () => props.itemValue,
      itemTitle: () => props.itemTitle,
      returnObject: () => props.returnObject,
      unavailableText: () => props.unavailableText,
    });

    const bar = () => h('span', {class: SKELETON_BAR, 'aria-hidden': 'true'});
    const prefix = () => (props.searchable ? 'v-autocomplete' : 'v-select');

    const renderChip = (scope) => {
      const id = scope.item?.value;
      const {class: _ghost, disabled: _d, ...chipProps} = scope.props ?? {};
      if (state.isPending(id)) {
        return h(VChip, {size: 'small', closable: false, text: '', class: 'tl-async-select__skel-chip', 'data-tl-skeleton': ''}, () => bar());
      }
      if (state.isUnavailable(id)) return h(VChip, {...chipProps, text: props.unavailableText});
      return slots.chip ? slots.chip(scope) : h(VChip, chipProps);
    };

    const renderSelection = (scope) => {
      const id = scope.item?.value;
      if (state.isPending(id)) return h('span', {class: `${prefix()}__selection-text`, 'data-tl-skeleton': ''}, [bar()]);
      if (state.isUnavailable(id)) return h('span', {class: `${prefix()}__selection-text`}, props.unavailableText);
      if (slots.selection) return slots.selection(scope);
      const last = scope.index >= state.selectedIds.value.length - 1;
      return h('span', {class: `${prefix()}__selection-text`}, [
        scope.item?.title,
        props.multiple && !last ? h('span', {class: `${prefix()}__selection-comma`}, ',') : null,
      ]);
    };

    return () => {
      const fieldSlots = {...slots};
      if (props.chips || slots.chip) {
        fieldSlots.chip = renderChip;
      } else if (!props.searchable || props.multiple || slots.selection) {
        // Stable across renders (v-autocomplete reads its slots once): a single plain autocomplete keeps Vuetify's
        // own inline title and gets a CSS skeleton through the pending class below instead.
        fieldSlots.selection = renderSelection;
      }
      const singlePending = props.searchable && !props.multiple && !props.chips && state.pendingIds.value.length > 0;
      const Field = props.searchable ? VAutocomplete : VSelect;
      return h(Field, {
        ...attrs,
        class: ['tl-async-select', {'tl-async-select--pending': state.pendingIds.value.length > 0, 'tl-async-select--pending-single': singlePending}, attrs.class],
        modelValue: props.modelValue,
        'onUpdate:modelValue': (v) => emit('update:modelValue', v),
        items: state.fieldItems.value,
        itemValue: props.itemValue,
        itemTitle: props.itemTitle,
        multiple: props.multiple,
        chips: props.chips,
        returnObject: props.returnObject,
        loading: props.loading || state.busy.value,
        readonly: props.readonly || state.busy.value,
      }, fieldSlots);
    };
  },
});
</script>

<style>
/* X199: skeleton in place of a not-yet-named selection; same line box, so the field keeps its height. Light kit. */
.tl-async-select__bar,
.tl-async-select--pending-single .v-autocomplete__selection-text::before {
  display: inline-block;
  width: 7.5em;
  max-width: 100%;
  height: 0.8em;
  border-radius: 4px;
  vertical-align: middle;
  background: rgba(var(--v-theme-on-surface), 0.1);
  animation: tl-async-select-pulse 1.2s ease-in-out infinite;
}
.tl-async-select--pending-single .v-autocomplete__selection-text::before { content: ''; }
.tl-async-select__skel-chip .tl-async-select__bar { width: 4.5em; height: 0.65em; }
.v-list-item.tl-async-select__ghost { display: none !important; }
@keyframes tl-async-select-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
}
@media (prefers-reduced-motion: reduce) {
  .tl-async-select__bar,
  .tl-async-select--pending-single .v-autocomplete__selection-text::before { animation: none; }
}
</style>
